import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Box, getCSSVariable, PressableBox, Row, Text, useMedia } from '@opendoor/bricks-next';
import AccountMenu from 'components/account/AccountMenu';

import { useObservability } from '../../../../../helpers/observability';
import { redirectToLogin } from './constants';
import styles from './GlassHeader.module.css';

interface GlassAccountSectionProps {
  isLoggedIn: boolean;
  showLogin: boolean;
  initials: string[];
}

export const GlassAccountSection: React.FC<GlassAccountSectionProps> = ({
  isLoggedIn,
  showLogin,
  initials,
}) => {
  const { trackEvent, trackJourneyEvent } = useObservability();
  const { largerThanSM } = useMedia();
  const [isExpanded, setExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = useCallback(() => setExpanded((prev) => !prev), []);
  const closeDropdown = useCallback(() => setExpanded(false), []);

  useEffect(() => {
    if (!isExpanded) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        closeDropdown();
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeDropdown();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isExpanded, closeDropdown]);

  const handleAvatarPress = useCallback(() => {
    trackEvent('cta-click', 'account-icon', undefined, { categoryOverride: 'homepage' });
    trackJourneyEvent('platform', 'buttonClickEvent', { targetName: 'user-account-menu-click' });
    toggleDropdown();
  }, [trackEvent, trackJourneyEvent, toggleDropdown]);

  const handleLoginPress = useCallback(() => {
    trackEvent('cta-click', 'account-icon', undefined, { categoryOverride: 'homepage' });
    trackJourneyEvent('platform', 'buttonClickEvent', { targetName: 'account-section-click' });
    redirectToLogin();
  }, [trackEvent, trackJourneyEvent]);

  if (isLoggedIn) {
    return (
      <Box
        ref={containerRef}
        position="relative"
        display="flex"
        alignItems="center"
        gap="$3"
        $smallerThanMD={{ display: 'none' }}
      >
        <PressableBox
          onPress={handleAvatarPress}
          analyticsName="cosmos-glass-nav-account"
          aria-label="User account menu"
          aria-haspopup="true"
          aria-expanded={isExpanded}
        >
          <Box
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            width="$8"
            height="$8"
            borderRadius="$full"
            $platform-web={{ background: 'rgba(255, 255, 255, 0.2)' }}
          >
            <Text tag="span" typography="$labelSmall" fontWeight="$medium" color="white">
              {initials}
            </Text>
          </Box>
        </PressableBox>
        {largerThanSM && <AccountMenu isExpanded={isExpanded} isLoggedIn={isLoggedIn} />}
      </Box>
    );
  }

  return (
    <Row alignItems="center" gap="$3" $smallerThanMD={{ display: 'none' }} ml="$12">
      {showLogin && (
        <PressableBox
          onPress={handleLoginPress}
          analyticsName="cosmos-glass-nav-signin"
          aria-label="Sign in to your account"
          borderRadius="$6x"
          cursor="pointer"
          backgroundColor="$transparent"
          style={{
            color: getCSSVariable('$contentInversePrimary', 'color'),
            border: `1px solid ${getCSSVariable('$contentInversePrimary', 'color')}`,
          }}
          className={styles.signInButton}
          $platform-web={{
            paddingBlock: '9.5px',
            paddingInline: '17.5px',
            letterSpacing: '-0.22px',
            textDecorationLine: 'none',
          }}
        >
          <Text tag="span" typography="$labelMedium" style={{ color: 'inherit' }}>
            Sign in
          </Text>
        </PressableBox>
      )}
    </Row>
  );
};
